import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import useLiveSync from "../lib/useLiveSync";
import { boxQty } from "../lib/stock";

const STATUS = { pulled: "Pulled", ready_for_pickup: "Ready for Pickup", picked_up: "Picked Up", sent_to_production: "Sent to Production" };
const localDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const shift = (day, n) => { const d = new Date(`${day}T12:00:00`); d.setDate(d.getDate() + n); return localDay(d); };

const Entry = ({ e }) => (
  <li className="py-3 flex gap-4" data-testid={`day-entry-${e.id}`}>
    <div className="w-16 shrink-0 text-xs text-muted-foreground tabular-nums pt-0.5">
      {new Date(e.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
    </div>
    <div className="min-w-0">
      <div className="text-sm">
        <span className="font-medium">{e.action}</span>
        {e.order_no && <span> · {e.order_no}</span>}
        {e.status && <span className="text-muted-foreground"> → {STATUS[e.status] || e.status}</span>}
      </div>
      <div className="text-xs text-muted-foreground">{e.user_name}</div>
      {e.stock_changes.length > 0 && (
        <div className="mt-1 space-y-0.5">
          {e.stock_changes.map((c, i) => (
            <div key={i} className="text-xs">
              {c.item} <span className="text-muted-foreground">@ {c.location_name}</span>: {boxQty(c.before, c.category)} → <b>{boxQty(c.after, c.category)}</b>
              <span className={c.change < 0 ? "text-destructive" : "text-[hsl(var(--accent-sage))]"}> ({c.change > 0 ? "+" : ""}{boxQty(c.change, c.category)})</span>
            </div>
          ))}
        </div>
      )}
    </div>
  </li>
);

export const KitchenDayLog = () => {
  const { user } = useAuth();
  const [day, setDay] = useState(localDay(new Date()));
  const [data, setData] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loc, setLoc] = useState("");
  const isAdmin = user?.role === "admin";
  useEffect(() => {
    if (isAdmin) api.get("/locations").then((r) => setLocations(r.data.filter((l) => l.is_warehouse)));
  }, [isAdmin]);
  const load = () => api.get(`/activity/daily?date=${day}&tz_offset=${new Date().getTimezoneOffset()}${loc ? `&location_id=${loc}` : ""}`)
    .then((r) => setData(r.data)).catch(() => setData({ entries: [] }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setData(null); load(); }, [day, loc]);
  useLiveSync(load, { interval: 15000 });
  const today = localDay(new Date());

  return (
    <div className="luxury-card p-6" data-testid="kitchen-day-log">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button onClick={() => setDay(shift(day, -1))} className="h-10 w-10 rounded-lg border border-border flex items-center justify-center hover:bg-secondary" data-testid="day-prev" aria-label="Previous day"><ChevronLeft className="h-4 w-4" /></button>
        <input type="date" value={day} max={today} onChange={(e) => e.target.value && setDay(e.target.value)} data-testid="day-picker"
          className="h-10 rounded-lg border border-input bg-card px-3 text-sm" />
        <button onClick={() => setDay(shift(day, 1))} disabled={day >= today} className="h-10 w-10 rounded-lg border border-border flex items-center justify-center hover:bg-secondary disabled:opacity-30" data-testid="day-next" aria-label="Next day"><ChevronRight className="h-4 w-4" /></button>
        {day !== today && <button onClick={() => setDay(today)} className="text-xs underline text-muted-foreground" data-testid="day-today">Today</button>}
        {isAdmin && (
          <select value={loc} onChange={(e) => setLoc(e.target.value)} data-testid="day-location" className="h-10 rounded-lg border border-input bg-card px-3 text-sm">
            <option value="">Kitchen</option>
            {locations.filter((l) => l.name !== "Kitchen").map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        )}
        <div className="ml-auto text-xs text-muted-foreground">{data ? `${data.entries.length} action${data.entries.length === 1 ? "" : "s"}` : ""}{data?.location_name ? ` · ${data.location_name}` : ""}</div>
      </div>
      {!data ? <div className="h-24 rounded shimmer" /> : data.entries.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground">Nothing recorded on this day.</div>
      ) : (
        <ul className="divide-y divide-border">{data.entries.map((e) => <Entry key={e.id} e={e} />)}</ul>
      )}
    </div>
  );
};
